<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { ContractManagementDateType } from '@/api';
import GaiaCheckbox from '../../../base/GaiaCheckbox.vue';
import GaiaCheckboxGroup from '../../../base/GaiaCheckboxGroup.vue';
import GaiaFormField from '../../../base/GaiaFormField.vue';
import GaiaText from '../../../base/GaiaText.vue';
import GaiaCalendarPromptStep from './GaiaCalendarPromptStep.vue';
import type { CalendarWizardForm } from '@/composables/useCalendarWizard';
import { DATE_TYPE_LABELS } from '@/services/contractManagement/dateRow';

const props = defineProps({
  form: {
    type: Object as PropType<CalendarWizardForm>,
    required: true,
  },
  touched: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:form': [form: CalendarWizardForm] }>();

function patch(changes: Partial<CalendarWizardForm>): void {
  emit('update:form', { ...props.form, ...changes });
}

const dateTypes = Object.values(ContractManagementDateType);

// Either source satisfies the rule, so the error sits once under the contract
// dates. An open-but-empty prompt is flagged by the prompt field itself.
const sourceError = computed(() =>
  props.touched && props.form.dateTypes.length === 0 && !props.form.useCustomPrompt
    ? 'Pick a contract date and/or add an extraction prompt before continuing'
    : undefined
);
</script>

<template>
  <n-flex vertical :size="18" :wrap="false">
    <n-flex vertical :size="4" :wrap="false">
      <gaia-text variant="label" size="micro" tone="faint">Contract dates</gaia-text>
      <gaia-text size="meta" tone="subtle">
        Dates already stored on each contract. Add them as they are, and add a prompt below if you
        need dates the contracts don’t store yet.
      </gaia-text>
    </n-flex>

    <gaia-form-field label="Date types" :error="sourceError">
      <gaia-checkbox-group
        :value="props.form.dateTypes"
        @update:value="
          (value: (string | number)[]) =>
            patch({ dateTypes: value as ContractManagementDateType[] })
        "
      >
        <n-flex :size="18">
          <gaia-checkbox
            v-for="dateType in dateTypes"
            :key="dateType"
            :value="dateType"
            :label="DATE_TYPE_LABELS[dateType]"
          />
        </n-flex>
      </gaia-checkbox-group>
    </gaia-form-field>

    <n-flex vertical :size="4" :wrap="false" :style="{ marginTop: '6px' }">
      <gaia-text variant="label" size="micro" tone="faint">Custom extraction</gaia-text>
      <gaia-text size="meta" tone="subtle">
        Insights runs your prompt on every contract the calendar scans and adds the dates it finds.
      </gaia-text>
    </n-flex>
    <gaia-checkbox
      label="Add own date extraction prompt"
      :checked="props.form.useCustomPrompt"
      @update:checked="(checked: boolean) => patch({ useCustomPrompt: checked })"
    />
    <gaia-calendar-prompt-step
      v-if="props.form.useCustomPrompt"
      :form="props.form"
      :touched="props.touched"
      @update:form="(next: CalendarWizardForm) => emit('update:form', next)"
    />
  </n-flex>
</template>
